import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Route title mappings
const routeTitles: Record<string, string> = {
  '/user/account': 'Account Info',
  '/user/update': 'Update Contact Info',
  '/user/publicprofile': 'Public Profile',
  '/user/public-profile': 'Public Profile',
  '/user/changepassword': 'Change Password',
  '/user/change-password': 'Change Password',
  '/user/confirm-email': 'Confirm Email',
  '/user/newsletter': 'Newsletter Opt-in',
  '/newsletter': 'Newsletter Opt-in',
  '/brands': 'My Brands',
  '/breweries': 'My Breweries',
  '/user/orders': 'Order History',
  '/orders': 'Order History',
  '/invoice': 'Pay Invoice',
  '/invoice/confirmEmail': 'Confirm Invoice Email',
  '/invoice/completed': 'Invoice Completed',
  '/user/press-releases': 'Press Releases',
  '/press-releases': 'Press Releases',
  '/help': 'Help',
};

// Pattern-based route titles (for dynamic routes)
const routePatterns: Array<{ pattern: RegExp; getTitleFromMatch: (matches: RegExpMatchArray) => string }> = [
  { pattern: /^\/breweries\/[^/]+\/edit(?:\/[^/]+)?$/, getTitleFromMatch: () => 'Brewery editor' },
  { pattern: /^\/breweries\/[^/]+\/beers\/new$/, getTitleFromMatch: () => 'Add beer' },
  { pattern: /^\/breweries\/[^/]+\/beers\/[^/]+\/edit$/, getTitleFromMatch: () => 'Edit beer' },
  {
    pattern: /^\/updatereview\/[^/]+$/,
    getTitleFromMatch: () => 'Brand Options',
  },
  {
    pattern: /^\/updatereview\/EditBrand\/[^/]+$/,
    getTitleFromMatch: () => 'Edit Brand',
  },
  {
    pattern: /^\/updatereview\/EditProducts\/[^/]+$/,
    getTitleFromMatch: () => 'Edit Products',
  },
  {
    pattern: /^\/updatereview\/CreateProduct\/[^/]+$/,
    getTitleFromMatch: () => 'Create Product',
  },
  {
    pattern: /^\/updatereview\/CreateSubBrand\/[^/]+$/,
    getTitleFromMatch: () => 'Create Sub-Brand',
  },
  {
    pattern: /^\/updatereview\/editProductInfo\/[^/]+\/[^/]+$/,
    getTitleFromMatch: () => 'Edit Product Info',
  },
  {
    pattern: /^\/updatereview\/UploadProductPhotos\/[^/]+\/[^/]+$/,
    getTitleFromMatch: () => 'Upload Product Photos',
  },
  {
    pattern: /^\/user\/press-releases\/upgrade\/[^/]+$/,
    getTitleFromMatch: () => 'Upgrade Press Release',
  },
  {
    pattern: /^\/user\/press-releases\/upgrade\/[^/]+\/success$/,
    getTitleFromMatch: () => 'Press Release Upgrade Success',
  },
];

/**
 * Custom hook to update document title based on current route
 * Improves accessibility by announcing page changes to screen readers
 */
export function usePageTitle() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    let title = routeTitles[path];

    // If no exact match, try pattern matching
    if (!title) {
      for (const { pattern, getTitleFromMatch } of routePatterns) {
        const matches = path.match(pattern);
        if (matches) {
          title = getTitleFromMatch(matches);
          break;
        }
      }
    }

    if (title) {
      document.title = `${title} | User Portal - BevNET CPG Media`;
    } else {
      document.title = 'User Portal - BevNET CPG Media';
    }

    // Announce page change to screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = title ? `Navigated to ${title}` : 'Page loaded';
    document.body.appendChild(announcement);

    // Remove announcement after a short delay
    const timeout = setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);

    return () => {
      clearTimeout(timeout);
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    };
  }, [location.pathname]);
}
